// Modal de visualização "Copiar pro CREA" — espelha campos da ART em formato
// pronto pra colar no portal CREA-RS campo-a-campo.
// Wave 2 (Antd) — 2026-06-01. Mantém SecaoCopia/CampoCopia helpers internos
// por terem identidade visual própria (header e botão copy).

import React, { useState } from 'react';
import {
  Modal, Button, Typography, App as AntApp, Card, Empty, Space, Alert,
} from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import type { Contrato, ArtFull } from './types';
import { fmtData } from './utils';

export function ModalArtCopiar({ art, contrato, onClose }: {
  art: ArtFull; contrato: Contrato; onClose: () => void;
}) {
  return <AntApp><ModalArtCopiarInner art={art} contrato={contrato} onClose={onClose} /></AntApp>;
}

function ModalArtCopiarInner({ art, onClose }: {
  art: ArtFull; contrato: Contrato; onClose: () => void;
}) {
  const { message } = AntApp.useApp();
  const [copiado, setCopiado] = useState<string | null>(null);

  function copiar(valor: any, key: string) {
    const s = valor == null || valor === '' ? '' : String(valor);
    if (!s) {
      message.warning('Campo vazio — nada pra copiar.');
      return;
    }
    navigator.clipboard.writeText(s).then(
      () => {
        setCopiado(key);
        setTimeout(() => setCopiado(c => c === key ? null : c), 1200);
      },
      () => message.error('Não foi possível copiar pra área de transferência.')
    );
  }

  function copiarTudo() {
    const linhas = [
      `=== Tipo de ART ===`,
      `Convênio: ${art.convenio || ''}`,
      `Tipo de ART: ${art.tipo_art || ''}`,
      `Empresa Executante: ${art.empresa_executante_nome || ''}`,
      `Nr.Reg empresa: ${art.empresa_executante_nr_reg || ''}`,
      `Participação Técnica: ${art.participacao_tecnica || ''}`,
      `Motivo da ART: ${art.motivo_art || ''}`,
      ``,
      `=== Contratado (RT) ===`,
      `Carteira: ${art.rt_carteira || ''}`,
      `Profissional: ${art.rt_nome || ''}`,
      `RNP: ${art.rt_rnp || ''}`,
      `Título: ${art.rt_titulo_profissional || ''}`,
      `E-mail: ${art.rt_email || ''}`,
      ``,
      `=== Contratante ===`,
      `Nome: ${art.contratante_nome || ''}`,
      `Telefone: ${art.contratante_telefone || ''}`,
      `CEP: ${art.contratante_cep || ''}`,
      `Logradouro: ${art.contratante_logradouro || ''}`,
      `Número: ${art.contratante_numero || ''}`,
      `Complemento: ${art.contratante_complemento || ''}`,
      `Bairro: ${art.contratante_bairro || ''}`,
      `Município: ${art.contratante_municipio || ''}`,
      `UF: ${art.contratante_uf || ''}`,
      ``,
      `=== Obra/Serviço ===`,
      `Proprietário: ${art.proprietario_nome || ''}`,
      `CEP: ${art.obra_cep || ''}`,
      `Tipo Logradouro: ${art.obra_tipo_logradouro || ''}`,
      `Logradouro: ${art.obra_logradouro || ''}`,
      `Número: ${art.obra_numero || ''}`,
      `Complemento: ${art.obra_complemento || ''}`,
      `Bairro: ${art.obra_bairro || ''}`,
      `Município: ${art.obra_municipio || ''}`,
      `UF: ${art.obra_uf || ''}`,
      `Finalidade: ${art.finalidade || ''}`,
      `Valor de Contrato: ${art.valor != null ? `R$ ${art.valor}` : ''}`,
      `Honorários: ${art.honorarios != null ? `R$ ${art.honorarios}` : ''}`,
      `Data Início: ${fmtData(art.inicio_art)}`,
      `Previsão Fim: ${fmtData(art.fim_art)}`,
      ``,
      `=== Atividades ===`,
    ];
    (art.atividades_linhas || []).forEach((l, i) => {
      linhas.push(`${i + 1}. ${l.cat_tecnica_nome || '(sem técnica)'} → ${l.cat_especifica_nome || '(sem específica)'} | ${l.quantidade} ${l.unidade}${l.descricao_complementar ? ` — ${l.descricao_complementar}` : ''}`);
    });
    linhas.push(``);
    linhas.push(`Resumo do contrato: ${art.resumo_contrato || ''}`);
    navigator.clipboard.writeText(linhas.join('\n')).then(
      () => { setCopiado('all'); setTimeout(() => setCopiado(null), 1500); message.success('Copiado tudo.'); },
      () => message.error('Não foi possível copiar.')
    );
  }

  return (
    <Modal open onCancel={onClose} width={760} footer={null}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        📋 Copiar pro CREA — ART {art.numero || '(sem número)'}
      </Typography.Title>}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
      <Alert type="info"
        message="Abra esta janela lado a lado com o portal CREA-RS e clique nos botões 📋 pra copiar cada valor."
        style={{ marginBottom: 14 }} />

      <Space style={{ marginBottom: 14 }}>
        <Button type={copiado === 'all' ? 'primary' : 'default'}
          icon={copiado === 'all' ? <CheckOutlined /> : <CopyOutlined />}
          onClick={copiarTudo}
          style={copiado === 'all' ? { background: '#0a7f4a', borderColor: '#0a7f4a' } : { borderColor: '#0a7f4a', color: '#0a7f4a' }}>
          {copiado === 'all' ? 'Copiado tudo' : 'Copiar tudo (texto)'}
        </Button>
      </Space>

      <SecaoCopia titulo="Tipo de ART (passo 1 do portal)">
        <CampoCopia label="Convênio" valor={art.convenio} chave="convenio" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Tipo de ART *" valor={art.tipo_art} chave="tipo_art" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Empresa Executante" valor={art.empresa_executante_nome} chave="empresa" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Nr.Reg empresa" valor={art.empresa_executante_nr_reg} chave="empresa_nr" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Participação Técnica" valor={art.participacao_tecnica} chave="part" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Motivo da ART *" valor={art.motivo_art} chave="motivo" copiado={copiado} onCopiar={copiar} />
      </SecaoCopia>

      <SecaoCopia titulo="Contratado (RT)">
        <CampoCopia label="Carteira" valor={art.rt_carteira} chave="rt_cart" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Profissional" valor={art.rt_nome} chave="rt_nome" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="E-mail" valor={art.rt_email} chave="rt_email" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="RNP" valor={art.rt_rnp} chave="rt_rnp" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Título" valor={art.rt_titulo_profissional} chave="rt_tit" copiado={copiado} onCopiar={copiar} />
      </SecaoCopia>

      <SecaoCopia titulo="Contratante (passo 2)">
        <CampoCopia label="Nome / Razão Social" valor={art.contratante_nome} chave="ct_nome" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Telefone" valor={art.contratante_telefone} chave="ct_fone" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="CEP" valor={art.contratante_cep} chave="ct_cep" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Logradouro" valor={art.contratante_logradouro} chave="ct_log" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Número" valor={art.contratante_numero} chave="ct_num" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Complemento" valor={art.contratante_complemento} chave="ct_comp" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Bairro" valor={art.contratante_bairro} chave="ct_bai" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Município" valor={art.contratante_municipio} chave="ct_mun" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="UF" valor={art.contratante_uf} chave="ct_uf" copiado={copiado} onCopiar={copiar} />
      </SecaoCopia>

      <SecaoCopia titulo="Obra/Serviço (passo 3)">
        <CampoCopia label="Proprietário" valor={art.proprietario_nome} chave="ob_prop" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="CEP da obra" valor={art.obra_cep} chave="ob_cep" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Tipo Logradouro" valor={art.obra_tipo_logradouro} chave="ob_tlog" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Logradouro" valor={art.obra_logradouro} chave="ob_log" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Número" valor={art.obra_numero} chave="ob_num" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Complemento" valor={art.obra_complemento} chave="ob_comp" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Bairro" valor={art.obra_bairro} chave="ob_bai" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Município" valor={art.obra_municipio} chave="ob_mun" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="UF" valor={art.obra_uf} chave="ob_uf" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Descrição livre" valor={art.endereco_obra} chave="ob_livre" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Finalidade" valor={art.finalidade} chave="finalidade" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Valor de Contrato *" valor={art.valor != null ? art.valor : ''} chave="valor" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Honorários" valor={art.honorarios != null ? art.honorarios : ''} chave="hono" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Data de Início *" valor={art.inicio_art ? String(art.inicio_art).slice(0,10) : ''} chave="ini" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Previsão de Fim *" valor={art.fim_art ? String(art.fim_art).slice(0,10) : ''} chave="fim" copiado={copiado} onCopiar={copiar} />
        <CampoCopia label="Resumo do contrato" valor={art.resumo_contrato} chave="resumo" copiado={copiado} onCopiar={copiar} />
      </SecaoCopia>

      <SecaoCopia titulo="Atividades (passo 4 — uma linha por vez)">
        {(art.atividades_linhas || []).length === 0 ? (
          <Empty description="Nenhuma linha de atividade." />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#F0F1F5', color: '#555' }}>
                <th style={{ padding: 6, textAlign: 'left' }}>Atividade Técnica</th>
                <th style={{ padding: 6, textAlign: 'left' }}>Atividade Específica</th>
                <th style={{ padding: 6, textAlign: 'right', width: 70 }}>Qtd</th>
                <th style={{ padding: 6, textAlign: 'left', width: 70 }}>Unid</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {art.atividades_linhas.map((l, i) => {
                const linhaTxt = `${l.cat_tecnica_nome || ''} | ${l.cat_especifica_nome || ''} | ${l.quantidade} ${l.unidade}${l.descricao_complementar ? ' | ' + l.descricao_complementar : ''}`;
                return (
                  <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                    <td style={{ padding: 6 }}>{l.cat_tecnica_nome || '—'}</td>
                    <td style={{ padding: 6 }}>{l.cat_especifica_nome || '—'}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{l.quantidade}</td>
                    <td style={{ padding: 6 }}>{l.unidade}</td>
                    <td style={{ padding: 6, textAlign: 'center' }}>
                      <Button size="small" type="text"
                        icon={copiado === 'lin' + i ? <CheckOutlined /> : <CopyOutlined />}
                        onClick={() => copiar(linhaTxt, 'lin' + i)}
                        style={{ color: copiado === 'lin' + i ? '#0a7f4a' : '#0930D0' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </SecaoCopia>
    </Modal>
  );
}

// Helpers visuais — preservados pra manter identidade do modal.
function SecaoCopia({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Card size="small" style={{ marginBottom: 14 }}
      title={<Typography.Text strong style={{ fontSize: 11, color: '#020D7F' }}>{titulo}</Typography.Text>}
      styles={{ body: { padding: 8 } }}>
      {children}
    </Card>
  );
}

function CampoCopia({ label, valor, chave, copiado, onCopiar }: {
  label: string; valor: any; chave: string;
  copiado: string | null; onCopiar: (v: any, k: string) => void;
}) {
  const v = valor == null || valor === '' ? null : String(valor);
  const isCopiado = copiado === chave;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 36px', gap: 8,
      padding: '4px 6px', alignItems: 'center', fontSize: 12,
      borderBottom: '0.5px solid #f4f4f4' }}>
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>{label}</Typography.Text>
      <span style={{ color: v ? '#222' : '#bbb',
        fontFamily: typeof valor === 'number' ? 'monospace' : 'inherit',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {v || <Typography.Text type="secondary" italic>(vazio)</Typography.Text>}
      </span>
      <Button size="small" type="text" disabled={!v}
        icon={isCopiado ? <CheckOutlined /> : <CopyOutlined />}
        onClick={() => onCopiar(v, chave)}
        title={v ? 'Copiar' : 'Vazio'}
        style={{ color: isCopiado ? '#0a7f4a' : (v ? '#0930D0' : '#bbb') }} />
    </div>
  );
}
